import React, { useState } from 'react';
import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';

const MergePdf = (props) => {
    const { isPopup = false } = props;
    const [pdfFiles, setPdfFiles] = useState([]);
    const [progress, setProgress] = useState(0);
    const [progressVisible, setProgressVisible] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);

    const fileInputRef = React.createRef();

    const handleFileSelect = (event) => {
        const files = event.target.files;
        const newFiles = Array.from(files).filter(file => file.type === 'application/pdf');
        setSelectedFiles(newFiles);
        setMergeBtnDisabled(newFiles.length < 2);
    };
    const addFilesToList = () => {
        const files = [...selectedFiles];
        if (selectedFiles.length > 0) {
            const newFiles = Array.from(files).filter(file => file.type === 'application/pdf');
            setPdfFiles(prevFiles => [...prevFiles, ...newFiles]);
            fileInputRef.current.value = '';
            if (pdfFiles.length + newFiles.length >= 2) {
                setMergeBtnDisabled(false);
            }
            setSelectedFiles([]);
        }
    };

    const removeFile = (index) => {
        const newFiles = [...pdfFiles];
        newFiles.splice(index, 1);
        setPdfFiles(newFiles);

        // Disable merge button if less than 2 files
        if (newFiles.length < 2) {
            setMergeBtnDisabled(true);
        }
    };

    const mergePdfFiles = async () => {
        try {
            setProgressVisible(true);
            setMergeBtnDisabled(true);

            const mergedPdf = await PDFDocument.create();

            for (let i = 0; i < pdfFiles.length; i++) {
                const file = pdfFiles[i];
                const fileArrayBuffer = await readFileAsArrayBuffer(file);
                const pdfDoc = await PDFDocument.load(fileArrayBuffer);
                const pages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
                pages.forEach(page => mergedPdf.addPage(page));

                const progress = Math.round(((i + 1) / pdfFiles.length) * 100);
                setProgress(progress);
            }

            const mergedPdfBytes = await mergedPdf.save();
            const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });

            const now = new Date();
            const dateStr = now.toISOString().slice(0, 10);
            const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '-');
            const filename = `merged_document_${dateStr}_${timeStr}.pdf`;

            saveAs(blob, filename);
            toast.success(`PDFs merged successfully!`);
            setTimeout(() => {
                setProgressVisible(false);
            }, 2000);
        } catch (error) {
            setMergeBtnDisabled(false);
            setProgressVisible(false);
        }
    };

    const readFileAsArrayBuffer = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) {
            return bytes + ' bytes';
        } else if (bytes < 1048576) {
            return (bytes / 1024).toFixed(2) + ' KB';
        } else {
            return (bytes / 1048576).toFixed(2) + ' MB';
        }
    };

    const [mergeBtnDisabled, setMergeBtnDisabled] = useState(true);

    return (
        <main className={isPopup ? 'mainPopup' : ''}>
            <div className={"main__container"}>
                <div className="main__tile_heading cus-btn-include-sec mb-0 mb-3">
                    <span>Merge PDFs
                        <div className='infoPermission'>
                            <i className="fa fa-lightbulb-o editorIdea" aria-hidden="true"></i>  Click the Add button to upload multiple PDF files. After uploading, click Merge to combine them into a single PDF.
                        </div>
                    </span>
                </div>

                <div className="cus-page-content-sec">
                    <div className="content-container pdfEditor">
                        <div className="file-input-container alignCenter">
                            <button className="btn-input-csv">
                                <label className="btn-chooseFile cursorPointer" htmlFor={`fileInput`}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        fill="currentColor"
                                        className="bi bi-upload mr-2"
                                        viewBox="0 0 16 16"
                                        cursor="pointer"
                                    >
                                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                                        <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z" />
                                    </svg> Select File
                                </label>
                            </button>
                            <input type="file" id="fileInput" ref={fileInputRef} accept=".pdf" multiple hidden onChange={handleFileSelect} />
                            <div className='alignCenter mt-3'>
                                {selectedFiles.length > 0 && (
                                    <span className="selected-files-count">{selectedFiles.length} file(s) selected</span>
                                )}
                                {selectedFiles.length > 0 && (
                                    <button className="btn cus-seconday-bg-btn top-btn ml-2 addFileBtn" onClick={addFilesToList}>Add Files</button>
                                )}
                            </div>
                        </div>

                        {pdfFiles.length > 0 && (
                            <div className="mt-3 fileMergeList">
                                <h3 className='mergeFileHeading'>Files to merge:</h3>
                                <ul id="fileList">
                                    {pdfFiles.map((file, index) => (
                                        <li key={uuidv4()}>
                                            <span>{file.name} ({formatFileSize(file.size)})</span>
                                            <button className="remove-btn" onClick={() => removeFile(index)}><i className="fa fa-trash" aria-hidden="true"></i></button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="progress-container" style={{ display: progressVisible ? 'block' : 'none' }}>
                            <div className="progress-bar" style={{ width: `${progress}%` }}></div>
                        </div>

                        {pdfFiles.length > 0 && (
                            <div className='mt-5 alignCenter'>
                                <button className="btn cus-seconday-bg-btn top-btn mergePdfs" onClick={mergePdfFiles} disabled={mergeBtnDisabled}>Merge PDFs</button>
                                <button className="btn cus-seconday-bg-btn top-btn clearAllBtn" onClick={() => setPdfFiles([])}>Clear All</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
};

MergePdf.propTypes = {
    isPopup: PropTypes.bool,
}

export default MergePdf;